/**
 * Étape 4 — Qui vient le chercher.
 *
 * L'étape qui bloquait en silence. Le formulaire d'origine exigeait, sans le
 * dire, soit une heure de départ seul, soit au moins une personne autorisée :
 * faute de l'un ou de l'autre, le clic sur « Suivant » ne produisait rien du
 * tout. Aucun message, aucun champ en rouge — un site qui a l'air cassé.
 *
 * La règle n'a pas changé, elle s'affiche. Et la question se pose désormais
 * dans l'ordre où on la vit : d'abord « part-il seul ? », ensuite « sinon,
 * qui ? ».
 */

import { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { AlertTriangle, Loader2, Pencil, Plus, Trash2, UserPlus, X } from 'lucide-react';
import clsx from 'clsx';
import { useAuthorizedPersonsStore } from '../../../stores/authorizedPersonsStore';
import { useAuthStore } from '../../../stores/authStore';
import AuthorizedPersonForm from '../AuthorizedPersonForm';
import { Bloc, Case, Navigation, Saisie } from './champs';
import type { Erreurs, Sorties } from './fiche';

const EtapeSorties = ({
  valeur,
  onChange,
  erreurs,
  onPrecedent,
  onSuivant,
  prenom,
}: {
  valeur: Sorties;
  onChange: (v: Sorties) => void;
  erreurs: Erreurs;
  onPrecedent: () => void;
  onSuivant: () => void;
  prenom: string;
}) => {
  const { persons, fetchPersons, deletePerson, addPerson } = useAuthorizedPersonsStore();
  const { profile, user } = useAuthStore();
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [ajoutParent, setAjoutParent] = useState(false);
  const [valeursProposees, setValeursProposees] = useState<
    { first_name: string; last_name: string; phone_number: string; relationship: string } | undefined
  >(undefined);
  const [personneEditee, setPersonneEditee] = useState<string | null>(null);
  const [suppressionId, setSuppressionId] = useState<string | null>(null);

  useEffect(() => {
    fetchPersons();
  }, [fetchPersons]);

  const maj = (champs: Partial<Sorties>) => onChange({ ...valeur, ...champs });
  const enfant = prenom.trim() || 'votre enfant';

  const basculer = (id: string) =>
    maj({
      personnesIds: valeur.personnesIds.includes(id)
        ? valeur.personnesIds.filter((p) => p !== id)
        : [...valeur.personnesIds, id],
    });

  /*
    Le parent qui remplit la fiche est presque toujours celui qui vient
    chercher l'enfant : on le propose d'office, sans le cocher — c'est au
    parent de dire oui. La proposition disparaît dès qu'il figure dans la liste.
  */
  const norm = (t: string | null | undefined) => (t ?? '').trim().toLowerCase();
  const prenomParent = profile?.prenom?.trim() ?? '';
  const nomParent = profile?.nom?.trim() ?? '';
  const parentConnu = !!(prenomParent && nomParent);
  const parentDejaListe = parentConnu
    ? persons.some((p) => norm(p.first_name) === norm(prenomParent) && norm(p.last_name) === norm(nomParent))
    : persons.length > 0;
  /*
    Une organisation (CPAS, maison d'accueil) n'est pas une personne qui vient
    chercher l'enfant : ce sont ses éducateurs, à ajouter un à un. La
    proposition « vous-même » n'a pas de sens pour elle.
  */
  const organisation = profile?.account_type === 'organisation';
  const proposerParent = !organisation && !parentDejaListe;
  const relation = profile?.account_type === 'other' ? 'Proche' : 'Parent';

  const cocherParent = async () => {
    const valeurs = {
      first_name: prenomParent,
      last_name: nomParent,
      phone_number: profile?.telephone?.trim() ?? '',
      relationship: relation,
    };
    // Sans nom ou sans téléphone, le formulaire s'ouvre pré-rempli
    if (!parentConnu || !valeurs.phone_number || !user) {
      setPersonneEditee(null);
      setValeursProposees(valeurs);
      setFormulaireOuvert(true);
      return;
    }
    setAjoutParent(true);
    try {
      // `user_id` comme dans AuthorizedPersonForm : la ligne appartient au parent connecté
      const creee = await addPerson({
        ...valeurs,
        user_id: user.id,
        photo_url: null,
      } as Parameters<typeof addPerson>[0]);
      await fetchPersons();
      onChange({ ...valeur, personnesIds: [...valeur.personnesIds, creee.id] });
    } catch {
      // En cas d'échec, le parent garde la main avec le formulaire pré-rempli
      setPersonneEditee(null);
      setValeursProposees(valeurs);
      setFormulaireOuvert(true);
    } finally {
      setAjoutParent(false);
    }
  };

  const supprimer = async (id: string) => {
    await deletePerson(id);
    maj({ personnesIds: valeur.personnesIds.filter((p) => p !== id) });
    setSuppressionId(null);
  };

  return (
    <div className="space-y-8">
      <Bloc
        titre="Le départ"
        intro={`Nous ne laissons partir ${enfant} qu'avec une personne que vous avez désignée — ou seul, si vous nous y autorisez.`}
      >
        <Case
          coche={valeur.partSeul}
          onChange={(v) => maj({ partSeul: v })}
          label={`${enfant} peut rentrer seul`}
          aide="À partir d’une heure que vous fixez."
        />

        {valeur.partSeul && (
          <div className="border-l-2 border-primary-100 pl-4">
            <Saisie
              label="À partir de quelle heure"
              requis
              type="time"
              valeur={valeur.heureDepart}
              onChange={(v) => maj({ heureDepart: v })}
              erreur={erreurs.heureDepart}
              aide={`Avant cette heure, ${enfant} reste avec le groupe.`}
            />
          </div>
        )}
      </Bloc>

      {!valeur.partSeul && (
        <Bloc
          titre="Les personnes autorisées"
          requis
          intro={
            organisation
              ? 'Ajoutez chaque éducateur ou accompagnant qui peut venir chercher l’enfant. Ces personnes sont partagées entre tous les enfants de votre organisation, vous ne les encoderez qu’une fois.'
              : 'Cochez qui est autorisé à venir chercher votre enfant. Ces personnes sont partagées entre tous vos enfants, vous ne les encoderez qu’une fois.'
          }
        >
          {persons.length === 0 && !proposerParent ? (
            <div
              className={clsx(
                'rounded-xl border-2 border-dashed p-8 text-center',
                erreurs.personnesIds ? 'border-red-300 bg-red-50/40' : 'border-gray-200',
              )}
            >
              <UserPlus className="mx-auto h-10 w-10 text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-900">
                Aucune personne autorisée pour l’instant
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {organisation
                  ? 'Ajoutez au moins un adulte\u00a0: un éducateur, un accompagnant…'
                  : 'Ajoutez au moins un adulte\u00a0: vous-même, l’autre parent, un grand-parent…'}
              </p>
              <button
                type="button"
                onClick={() => {
                  setPersonneEditee(null);
                  setValeursProposees(undefined);
                  setFormulaireOuvert(true);
                }}
                className="btn-primary mt-4 inline-flex items-center"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Ajouter une personne
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {proposerParent && (
                <label
                  className={clsx(
                    'flex cursor-pointer items-center gap-3 rounded-lg border border-dashed p-3 transition-colors hover:border-gray-400',
                    erreurs.personnesIds ? 'border-red-300' : 'border-gray-300',
                  )}
                >
                  {ajoutParent ? (
                    <Loader2 className="h-4 w-4 animate-spin text-primary-600" aria-hidden="true" />
                  ) : (
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={cocherParent}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  )}
                  <span>
                    <span className="block text-sm font-medium text-gray-900">
                      {parentConnu ? `${prenomParent} ${nomParent}` : 'Vous-même'}
                      <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-normal text-gray-600">
                        vous
                      </span>
                    </span>
                    <span className="block text-sm text-gray-500">
                      {relation}
                      {profile?.telephone ? ` · ${profile.telephone}` : ' · téléphone à compléter'}
                    </span>
                  </span>
                </label>
              )}

              {persons.map((p) => {
                const choisie = valeur.personnesIds.includes(p.id);
                return (
                  <div
                    key={p.id}
                    className={clsx(
                      'flex items-center gap-3 rounded-lg border p-3 transition-colors',
                      choisie
                        ? 'border-primary-500 bg-primary-50'
                        : erreurs.personnesIds
                          ? 'border-red-300'
                          : 'border-gray-200 hover:border-gray-300',
                    )}
                  >
                    <label className="flex flex-1 cursor-pointer items-center gap-3">
                      <input
                        type="checkbox"
                        checked={choisie}
                        onChange={() => basculer(p.id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-900">
                          {p.first_name} {p.last_name}
                        </span>
                        <span className="block text-sm text-gray-500">
                          {p.relationship} · {p.phone_number}
                        </span>
                      </span>
                    </label>
                    <button
                      type="button"
                      aria-label={`Modifier ${p.first_name}`}
                      onClick={() => {
                        setPersonneEditee(p.id);
                        setValeursProposees(undefined);
                        setFormulaireOuvert(true);
                      }}
                      className="p-1.5 text-gray-400 hover:text-gray-600"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Supprimer ${p.first_name}`}
                      onClick={() => setSuppressionId(p.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}

              <button
                type="button"
                onClick={() => {
                  setPersonneEditee(null);
                  setValeursProposees(undefined);
                  setFormulaireOuvert(true);
                }}
                className="btn-outline inline-flex items-center"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Ajouter une personne
              </button>
            </div>
          )}

          {erreurs.personnesIds && (
            <p className="text-sm text-red-600">{erreurs.personnesIds}</p>
          )}
        </Bloc>
      )}

      <Navigation onPrecedent={onPrecedent} onSuivant={onSuivant} />

      {/* Ajout / modification d'une personne */}
      <Dialog
        open={formulaireOuvert}
        onClose={() => setFormulaireOuvert(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <Dialog.Panel className="relative mx-auto w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <button
              type="button"
              onClick={() => setFormulaireOuvert(false)}
              aria-label="Fermer"
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
            <Dialog.Title className="mb-4 text-lg font-semibold">
              {personneEditee ? 'Modifier une personne' : 'Ajouter une personne'}
            </Dialog.Title>
            <AuthorizedPersonForm
              initialData={personneEditee ? persons.find((p) => p.id === personneEditee) : undefined}
              valeursProposees={valeursProposees}
              onComplete={async () => {
                setFormulaireOuvert(false);
                /*
                  La liste est relue avant de cocher : une personne qui vient
                  d'être créée n'a d'identifiant qu'une fois enregistrée, et
                  cocher pour le parent lui épargne un geste qu'il oublierait.
                */
                const avant = new Set(persons.map((p) => p.id));
                await fetchPersons();
                const apres = useAuthorizedPersonsStore.getState().persons;
                const nouvelle = apres.find((p) => !avant.has(p.id));
                if (nouvelle) {
                  onChange({
                    ...valeur,
                    personnesIds: [...valeur.personnesIds, nouvelle.id],
                  });
                }
              }}
            />
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Confirmation de suppression */}
      <Dialog
        open={!!suppressionId}
        onClose={() => setSuppressionId(null)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <Dialog.Panel className="relative mx-auto w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center gap-3">
              <span className="rounded-full bg-red-100 p-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </span>
              <Dialog.Title className="text-lg font-semibold">Supprimer cette personne ?</Dialog.Title>
            </div>
            <p className="mb-6 text-gray-600">
              Elle sera retirée de tous vos enfants, et cette action est irréversible.
            </p>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setSuppressionId(null)} className="btn-outline">
                Annuler
              </button>
              <button
                type="button"
                onClick={() => suppressionId && supprimer(suppressionId)}
                className="btn bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
              >
                Supprimer
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default EtapeSorties;
